<template>
  <b-card
    class="mb-05 product-row"
    :class="{
      'cursor-pointer hover-bg': validRoute,
      'hover-candy': currentUser.role_name !== 'admin',
      'product-row--selected': selected,
      'product-row--selectable': selectable,
    }"
    no-body
  >
    <b-dropdown
      v-if="showActionsMenu"
      id="dropdown-1"
      toggle-class="my-custom-toggle"
      size="sm"
      right
      no-caret
      variant="outline-none"
      data-bs-toggle="dropdown"
    >
      <template #button-content>
        <feather-icon
          icon="MoreVerticalIcon"
          size="18"
          color="#000"
        />
      </template>
      <b-dropdown-item
        v-if="$route.name === 'productsCatalogs'"
        :to="{ name: 'editProduct', params: { productId: product.id } }"
      >{{ $t('edit') }}
      </b-dropdown-item>
      <b-dropdown-item
        v-if="canDiscardForBranch"
        v-b-modal="`confirmDiscard-${product.id}`"
      >
        <span class="text-danger d-flex align-items-center">
          <feather-icon
            icon="Trash2Icon"
            size="14"
            class="mr-50"
          />
          Enviar a papelera
        </span>
      </b-dropdown-item>
      <b-dropdown-item
        v-if="canManageGlobalProduct && product.active_status"
        v-b-modal="`confirmDelete-${product.id}`"
      >
        <span class="text-danger">
          {{ $t('delete') }}
        </span>
      </b-dropdown-item>
      <b-dropdown-item
        v-if="canManageGlobalProduct && !product.active_status"
        v-b-modal="`confirmReactivate-${product.id}`"
      >
        <span class="text-success">
          {{ $t('reactivate') }}
        </span>
      </b-dropdown-item>
    </b-dropdown>
    <b-row class="product-row__content no-gutters">
      <b-col
        v-if="selectable"
        cols="auto"
        class="selection-cell"
      >
        <button
          type="button"
          class="selection-toggle"
          :class="{ 'selection-toggle--selected': selected }"
          :aria-pressed="selected ? 'true' : 'false'"
          @click="$emit('selection-changed', { product, selected: !selected })"
        >
          <feather-icon
            :icon="selected ? 'CheckIcon' : 'PlusIcon'"
            size="14"
          />
        </button>
      </b-col>
      <b-col class="d-flex product-main-cell">
        <div class="pr-1">
          <b-img
            v-if="false"
            style="height:90px"
            width="90px"
            :src="`${product.logo}`"
          />
        </div>
        <div>
          <h4 class="mb-0 mt-1">
            {{ product.name }}
          </h4>
          <div class="d-flex flex-column">
            <span
              class="text-muted text-small mb-0"
            >{{ product.sku }} -
              <span class="font-weight-bolder text-dark">
                {{ product.brand || 'N/A' }}
              </span>
            </span>
            <div>
              <b-badge
                pill
                variant="primary"
                class="text-small mb-1"
              >
                {{ product.category.name || '' }}
              </b-badge>
            </div>
            <div v-if="product.product_tags && product.product_tags.length">
              <b-badge
                v-for="tag in product.product_tags"
                :key="`tag-${tag.id}`"
                pill
                variant="light-info"
                class="text-small mb-25 mr-25"
              >
                {{ tag.name }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-col>
      <b-col class="d-none d-md-block">
        <div class="px-2">
          <h5 class="mt-1 text-small">
            {{ $t('description') }}
          </h5>
          <div class="text-justify text-muted">
            {{ product.description }}
          </div>
        </div>
      </b-col>
      <b-col
        v-if="validRoute"
        xs="12"
        sm="6"
        md="4"
        class="d-flex justify-content-end align-items-center mb-1 my-sm-0 mr-1 mx-sm-0 product-action-cell"
      >
        <b-form-group class="m-0 mr-1">
          <b-input-group size="sm">
            <b-form-input
              v-model="quantity"
              :type="isRegisteringSupplierProduct ? 'text' : 'number'"
              :inputmode="isRegisteringSupplierProduct ? 'decimal' : null"
              :min="isRegisteringSupplierProduct ? 0 : 1"
              :step="isRegisteringSupplierProduct ? '0.000001' : '0.01'"
              class="qty-field"
              :placeholder="$route.name === 'registerSupplierProduct' ? 'Precio' : $t('quantity')"
              autocomplete="off"
              :state="quantityValid"
              :formatter="limitDecimalPlaces"
              @input="setQuantity"
            />
            <b-input-group-append is-text>
              {{ $tc(unitsFilter(product.unit_type)) }}
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        <b-button
          size="sm"
          variant="outline-primary"
          class="d-flex custom-button"
          @click="add()"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
          />
        </b-button>
      </b-col>
      <b-col cols="auto" />
    </b-row>
    <confirmation-modal
      :id="`confirmDiscard-${product.id}`"
      :variant="'danger'"
      @confirm="discardProduct()"
    >
      <template v-slot:content>
        Este producto se ocultará de los productos disponibles para esta sucursal.
      </template>
    </confirmation-modal>
    <confirmation-modal
      :id="`confirmDelete-${product.id}`"
      :variant="'danger'"
      @confirm="deleteProduct()"
    >
      <template v-slot:content>
        {{ $t('deleteProductConfirmation') }}
      </template>
    </confirmation-modal>
    <confirmation-modal
      :id="`confirmReactivate-${product.id}`"
      :variant="'success'"
      @confirm="reactivateProduct()"
    >
      <template v-slot:content>
        {{ $t('reactivateProductConfirmation') }}
      </template>
    </confirmation-modal>
  </b-card>
</template>

<script>
import { BCard } from 'bootstrap-vue'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    ConfirmationModal,
  },
  props: {
    product: {
      type: Object,
      default() {
        return {}
      },
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      quantity: null,
      quantityValid: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('app', ['unitsFilter']),
    ...mapGetters('groupedExpenses', ['expenseType']),
    validRoute() {
      return (
        this.$route.name === 'registerSupplierProduct'
        || this.$route.name === 'addProductsToQuote'
        || this.$route.name === 'addGroupedExpenses'
      )
    },
    isRegisteringSupplierProduct() {
      return this.$route.name === 'registerSupplierProduct'
    },
    maxDecimalPlaces() {
      return this.isRegisteringSupplierProduct ? 6 : 2
    },
    canDiscardForBranch() {
      return this.$route.name === 'registerSupplierProduct'
        && this.$route.params.type === 'sucursal'
    },
    canManageGlobalProduct() {
      return this.currentUser
        && this.currentUser.role_name === 'admin'
        && this.$route.name === 'productsCatalogs'
    },
    showActionsMenu() {
      return this.canManageGlobalProduct || this.canDiscardForBranch
    },
    breakpoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    isSmallScreen() {
      switch (this.$store.getters['app/currentBreakPoint']) {
        case 'md':
        case 'sm':
        case 'xs':
          return true
        default:
          return false
      }
    },
  },
  methods: {
    ...mapMutations('pos', {
      addItem: 'ADD_ITEM_TO_POS',
    }),
    ...mapActions('products', ['deactivateProduct', 'fetchProducts', 'discardStockableProduct']),

    add() {
      if (!this.validateDecimals()) {
        return
      }
      if (this.$route.name === 'addGroupedExpenses') {
        this.addItem({
          ...this.product,
          expense_type_catalog_id: this.expenseType,
          isMaterial: true,
        })
      } else {
        this.addItem({ ...this.product, quantity: this.quantity })
      }
    },

    deleteProduct() {
      this.deactivateProduct(this.product.id).then(() => {
        this.fetchProducts({
          by_active_status: true,
          meta: {
            pagination: {
              per_page: 100,
            },
          },
        })
        this.$emit('product-updated')
      })
    },
    discardProduct() {
      this.discardStockableProduct({
        stockable_id: this.$route.params.id,
        stockable_type: 'SupplierBranch',
        product_catalog_id: this.product.id,
      }).then(() => {
        this.$emit('product-updated')
      })
    },
    reactivateProduct() {
      this.deactivateProduct(this.product.id).then(() => {
        this.fetchProducts({
          by_active_status: false,
          meta: {
            pagination: {
              per_page: 100,
            },
          },
        })
        this.$emit('product-updated')
      })
    },
    validateDecimals() {
      if (this.quantity === null || this.quantity === '') {
        this.quantityValid = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$route.name === 'registerSupplierProduct'
              ? 'Captura el precio unitario'
              : 'Captura la cantidad',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return false
      }
      const decimalPart = String(this.quantity).split('.')[1]
      if (decimalPart && decimalPart.length > this.maxDecimalPlaces) {
        this.quantityValid = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `Solo se permiten ${this.maxDecimalPlaces} decimales`,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return false
      }
      return true
    },
    limitDecimalPlaces(value) {
      const stringValue = String(value ?? '').replace(',', '.')
      const [integerPart, ...decimalParts] = stringValue.split('.')
      const sanitizedInteger = integerPart.replace(/\D/g, '')
      const decimalPart = decimalParts.join('').replace(/\D/g, '')

      if (decimalParts.length === 0) return sanitizedInteger

      return `${sanitizedInteger}.${decimalPart.slice(0, this.maxDecimalPlaces)}`
    },
    setQuantity(value) {
      this.quantity = this.limitDecimalPlaces(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.product-row {
  border: 1px solid transparent;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(34, 41, 47, 0.045);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.product-row--selectable {
  overflow: hidden;
}

.product-row--selected {
  border-color: #340668;
  background-color: #fbf9ff;
  box-shadow: 0 6px 18px rgba(52, 6, 104, 0.11);
}

.product-row__content {
  min-height: 76px;
}

.product-main-cell {
  padding-left: 1rem;
}

.selection-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  min-width: 52px;
  padding: 0;
  background: #f7f5fa;
  border-right: 1px solid #ebe6f0;
}

.selection-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  border: 1px solid #cfc7dc;
  border-radius: 8px;
  background: #fff;
  color: #6d6380;
  cursor: pointer;
  line-height: 1;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.selection-toggle:hover {
  border-color: #340668;
  color: #340668;
}

.selection-toggle--selected {
  border-color: #340668;
  background: #340668;
  color: #fff;
}

.product-action-cell {
  padding-right: 1rem;
}

.bg-best-price {
  background-color: #77c77b;
}
.mb-05 {
  margin-bottom: 0.5rem;
}
.text-small {
  font-size: 10px;
}
.hover-bg:hover {
  background-color: rgba(52, 6, 104, 0.12);
}
.hover-candy:hover {
  transform: translateY(-3px);
}
.custom-button {
  padding: 4px 4px;
}
.qty-field {
  max-width: 130px;
}
#dropdown-1 {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1000;
}
</style>
